// The Phase 1 synthetic scenario (docs/design/phase1-ui-handoff.md): one authoritative object per
// stage. Every panel reads from the active stage; nothing is kept elsewhere. Synthetic only
// (CLAUDE.md hard rule 1). Risk and Priority Score are the pack's fixed demonstration outputs,
// never computed (hard rule 3 exception of 2026-10-31, docs/design/phase1-ui-decisions.md).

export interface Vital {
  key: 'gcs' | 'hr' | 'bp' | 'rr' | 'spo2';
  label: string;
  value: string;
  unit: string;
  /** Clinical record time (synthetic), HH:MM UTC. */
  at: string;
  source: string;
}

export interface EvidenceItem {
  label: string;
  at: string;
  source: string;
}

export interface StageAlert {
  title: string;
  detail: string;
  action: string;
  prior: { key: Vital['key']; value: string; at: string; source: string };
  next: { key: Vital['key']; value: string; at: string; source: string };
}

export interface Stage {
  number: 1 | 2 | 3 | 4;
  label: string;
  /** Clinical record time (synthetic), HH:MM UTC. Never the simulation clock. */
  recordTime: string;
  newInformation: string;
  evidenceSource: string;
  vitals: Vital[];
  /** Other recorded items shown as evidence (notes, lab panel). */
  evidence: EvidenceItem[];
  availability: {
    level: string;
    available: string;
    /** "Not yet available: …" or, from Stage 4, what this view does not provide. */
    unavailable: string;
  };
  /** Stage 1 only; later stages derive "What changed" from the previous stage. */
  changeText?: string;
  alert?: StageAlert;
  reviewStatus: string;
  /**
   * "Dynamic clinical state": the recorded values and their direction of change, in words. Only
   * values of this stage and the previous one; no diagnosis, interpretation or action.
   */
  physiology: string;
  /** Fixed demonstration output from the pack, in percent. Never computed. */
  risk: number;
  /** Fixed demonstration output from the pack, out of 100. Never computed. */
  priorityScore: number;
  /** Signals shown as contributors; their values come from `vitals`. */
  contributors: Vital['key'][];
}

export const PATIENT = {
  name: 'Bryan Adams',
  context: 'Bryan Adams · 68M · Shared patient context',
  complaint: 'Sudden onset right-sided weakness and speech difficulty; exact onset time unknown.',
  historySource: 'Family historian; reliability recorded as moderate; exact onset time unknown.',
};

export const COPY = {
  boundary:
    'Predefined simulation using authorised scenario information. Not a validated prediction or autonomous clinical decision.',
  footer:
    'Simulated demonstration data. Supports clinician situational awareness only. Clinician judgement remains required.',
  initialCondition: '07:00 UTC · GCS 8 · HR 95 bpm · BP 187/100 mmHg · SpO2 95%',
  initialSource: 'Source: ED registration and triage record',
  intervalExplanation: 'Demonstration speed only; not clinical-record time.',
  clinicalTimeLabel: 'Clinical record time — simulation controls are demonstration speed only.',
  normalReview: 'Review status: monitor evolving simulated record.',
  stage4Review:
    'Review status: reassessment recommended from newly displayed simulated information.',
  imagingBoundary: 'No direct image interpretation in this view.',
  phaseBoundary:
    'This Phase 1 demonstration uses a predefined synthetic clinical scenario. It may display authorised scenario-specific information, including condition-related observations and recorded workflow events. It does not generate a diagnosis, treatment recommendation, pathway activation, triage decision, ICU admission decision, transfer decision, or autonomous escalation decision.',
  observationOnly:
    'Observation only: this describes recorded values and how they changed. It does not assign a diagnosis or prescribe a clinical action.',
  clinicianView: 'Simulated demonstration data — not a clinically validated prediction.',
  riskTitle: 'Clinical Deterioration Risk',
  scoreTitle: 'Diagnuvo Priority Score',
  outputLabel: 'Predefined simulation output',
  outputBoundary:
    'Fixed demonstration value — not a clinically validated probability or clinical assessment.',
  contributorSignal: 'Simulation signal',
  contributorObservation: 'Available observation',
  contributorNote:
    'Signals available in this predefined stage; no causal feature weights are shown.',
  prognosticNotConfigured: 'Not configured for this synthetic demonstration stage.',
  prognosticBoundary:
    'Predefined synthetic demonstration output. Not clinically validated, not a clinical assessment, and not a decision or recommendation about patient management.',
  stage5Pending:
    'Current simulated record consolidated — awaiting approved consolidation details (Stage 5 GCS provenance gate).',
};

const vitals = (
  at: string,
  source: string,
  v: { gcs: string; hr: string; bp: string; rr?: string; spo2: string },
  gcsSource = source,
): Vital[] => [
  { key: 'gcs', label: 'GCS', value: v.gcs, unit: '', at, source: gcsSource },
  { key: 'hr', label: 'HR', value: v.hr, unit: 'bpm', at, source },
  { key: 'bp', label: 'BP', value: v.bp, unit: 'mmHg', at, source },
  ...(v.rr === undefined
    ? []
    : [{ key: 'rr' as const, label: 'RR', value: v.rr, unit: '/min', at, source }]),
  { key: 'spo2', label: 'SpO2', value: v.spo2, unit: '%', at, source },
];

export const STAGES: readonly Stage[] = [
  {
    number: 1,
    label: 'Initial observation window opened',
    recordTime: '07:00',
    newInformation: 'Initial baseline record',
    evidenceSource: 'ED registration and triage record',
    vitals: vitals('07:00', 'ED registration and triage record', {
      gcs: '8',
      hr: '95',
      bp: '187/100',
      spo2: '95',
    }),
    evidence: [
      { label: 'History source', at: '07:00', source: 'Family historian, exact onset unknown' },
    ],
    availability: {
      level: 'Limited baseline',
      available: 'triage, current vital signs, history source',
      unavailable: 'Not yet available: laboratory results, imaging report, specialist note',
    },
    changeText: 'Initial baseline established. No later simulated information is available.',
    reviewStatus: COPY.normalReview,
    physiology:
      'The initial record contains GCS 8, blood pressure 187/100 mmHg, heart rate 95 bpm and SpO2 95%. No later information has been added yet.',
    risk: 15,
    priorityScore: 15,
    contributors: ['gcs', 'bp'],
  },
  {
    number: 2,
    label: 'Early physiological change recorded',
    recordTime: '07:20',
    newInformation: 'New vital observation and initial assessment note',
    evidenceSource: 'Bedside monitor and initial physician note',
    vitals: vitals('07:20', 'Bedside monitor', {
      gcs: '8',
      hr: '96',
      bp: '189/100',
      rr: '19',
      spo2: '95',
    }),
    evidence: [
      { label: 'History source', at: '07:00', source: 'Family historian, exact onset unknown' },
      { label: 'Initial assessment note', at: '07:20', source: 'Initial physician note' },
    ],
    availability: {
      level: 'Moderate baseline',
      available: 'second vital observation, initial assessment note, history source',
      unavailable: 'Not yet available: laboratory results, imaging report, specialist note',
    },
    reviewStatus: COPY.normalReview,
    physiology:
      'Heart rate and blood pressure are slightly higher (96 bpm, 189/100 mmHg). GCS remains 8 and SpO2 is unchanged at 95%. Respiratory rate is recorded for the first time: 19 /min.',
    risk: 28,
    priorityScore: 32,
    contributors: ['hr', 'bp', 'rr'],
  },
  {
    number: 3,
    label: 'Laboratory information became available',
    recordTime: '15:18',
    newInformation: 'Reported laboratory results',
    evidenceSource: 'Hospital laboratory information system',
    vitals: vitals('15:18', 'Current vital signs in the synthetic record', {
      gcs: '8',
      hr: '98',
      bp: '193/103',
      rr: '20',
      spo2: '94',
    }),
    evidence: [
      { label: 'History source', at: '07:00', source: 'Family historian, exact onset unknown' },
      { label: 'Initial assessment note', at: '07:20', source: 'Initial physician note' },
      {
        label: 'Laboratory panel reported',
        at: '15:18',
        source: 'Hospital laboratory information system',
      },
    ],
    availability: {
      level: 'Moderate–High',
      available: 'vitals, clinical note, laboratory panel, history source',
      unavailable: 'Not yet available: later nursing observation, later timeline information',
    },
    reviewStatus: COPY.normalReview,
    physiology:
      'Blood pressure and heart rate are higher (193/103 mmHg, 98 bpm), respiratory rate is 20 /min and SpO2 is lower at 94%. GCS remains 8. A laboratory panel has been reported.',
    risk: 38,
    priorityScore: 54,
    contributors: ['bp', 'hr', 'spo2'],
  },
  {
    number: 4,
    label: 'Additional physiological change recorded',
    recordTime: '15:35',
    newInformation: 'New nursing and physiological observations',
    evidenceSource: 'Nursing flowsheet and observation telemetry',
    vitals: vitals(
      '15:35',
      'Observation telemetry',
      { gcs: '7', hr: '102', bp: '198/106', rr: '22', spo2: '93' },
      'Nursing documentation',
    ),
    evidence: [
      { label: 'History source', at: '07:00', source: 'Family historian, exact onset unknown' },
      { label: 'Initial assessment note', at: '07:20', source: 'Initial physician note' },
      {
        label: 'Laboratory panel reported',
        at: '15:18',
        source: 'Hospital laboratory information system',
      },
      { label: 'Nursing documentation', at: '15:35', source: 'Nursing flowsheet' },
    ],
    availability: {
      level: 'High for available entries',
      available: 'vitals, nursing documentation, laboratory panel, history source',
      unavailable: 'Not in this view: no direct image interpretation; no model recommendation',
    },
    alert: {
      title: 'New simulated neurological observation',
      detail: 'GCS changed from 8 at 15:18 UTC to 7 at 15:35 UTC. Source: Nursing documentation.',
      action:
        'Clinician reassessment is recommended. Simulated demonstration data only; no autonomous clinical decision.',
      prior: {
        key: 'gcs',
        value: '8',
        at: '15:18',
        source: 'Current vital signs in the synthetic record',
      },
      next: { key: 'gcs', value: '7', at: '15:35', source: 'Nursing documentation' },
    },
    reviewStatus: COPY.stage4Review,
    physiology:
      'GCS is lower: 7, previously 8. Blood pressure, heart rate and respiratory rate are higher (198/106 mmHg, 102 bpm, 22 /min) and SpO2 is lower at 93%.',
    risk: 48,
    priorityScore: 72,
    contributors: ['gcs', 'bp', 'rr', 'spo2'],
  },
];

export interface Contributor {
  vital: Vital;
  /** "Simulation signal" when the value changed from the previous stage. */
  tag: string;
}

/** Contributors of a stage, with values from its own vitals (handoff rule 1). */
export function contributors(stage: Stage): Contributor[] {
  const changed = new Set(changes(stage).map((c) => c.label));
  return stage.contributors.flatMap((key) => {
    const vital = stage.vitals.find((v) => v.key === key);
    if (vital === undefined) return [];
    const tag = changed.has(vital.label) ? COPY.contributorSignal : COPY.contributorObservation;
    return [{ vital, tag }];
  });
}

/** Stages in the pack's sequence; Stage 5 exists but is gated (not reachable). */
export const TOTAL_STAGES = 5;

export interface Change {
  label: string;
  from?: string;
  to: string;
  unit: string;
  at: string;
  source: string;
}

/** What changed from the previous stage: every vital whose value differs, as recorded. */
export function changes(stage: Stage): Change[] {
  const previous = STAGES[stage.number - 2];
  if (previous === undefined) return [];
  return stage.vitals.flatMap((v) => {
    const before = previous.vitals.find((p) => p.key === v.key);
    if (before?.value === v.value) return [];
    return [
      {
        label: v.label,
        ...(before !== undefined && { from: before.value }),
        to: v.value,
        unit: v.unit,
        at: v.at,
        source: v.source,
      },
    ];
  });
}
